import { useState } from "react";
import usePageLoader from '../../hooks/usePageLoader';
import { ShimmerBox, ShimmerFormField } from '../../components/common/Shimmer';
import {
  FiSave, FiPlus, FiX, FiEye, FiEyeOff, FiAlertTriangle,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

const TABS = [
  { key: "general",       label: "General" },
  { key: "leadcapture",   label: "Lead Capture" },
  { key: "chatbehavior",  label: "Chat Behavior" },
  { key: "notifications", label: "Notifications" },
  { key: "localization",  label: "Localization" },
  { key: "advanced",      label: "Advanced" },
];

function SettingsSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-6 space-y-5">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white border rounded-xl p-6 space-y-4">
          <ShimmerBox h="h-4" w="w-36" />
          <div className="space-y-4">
            {[...Array(3)].map((_, j) => <ShimmerFormField key={j} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SettingsPage() {
  const { chatbotId } = useParams();
  const { selected } = useSelector((s) => s.chatbots);
  const [activeTab, setActiveTab] = useState("general");
  const [saving, setSaving] = useState(false);
  const loading = usePageLoader();

  const handleSave = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 800));
    setSaving(false);
    toast.success(`${TABS.find((t) => t.key === activeTab)?.label} settings saved!`);
  };

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* Sticky header */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Configure {selected?.chatbot_name || "your chatbot"}'s behaviour and integrations
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 transition disabled:opacity-70"
        >
          {saving
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <FiSave size={14} />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {/* Sticky tab bar */}
      <div className="shrink-0 bg-white border-b px-6 py-3">
        <div className="flex items-center border rounded-lg overflow-hidden text-sm font-medium w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap px-4 py-2 transition ${
                activeTab === tab.key ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <SettingsSkeleton /> : <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-2xl mx-auto py-6 px-4 space-y-5">
          {activeTab === "general"       && <GeneralTab botName={selected?.chatbot_name} />}
          {activeTab === "leadcapture"   && <LeadCaptureTab />}
          {activeTab === "chatbehavior"  && <ChatBehaviorTab />}
          {activeTab === "notifications" && <NotificationsTab />}
          {activeTab === "localization"  && <LocalizationTab />}
          {activeTab === "advanced"      && <AdvancedTab />}
        </div>
      </div>}

    </div>
  );
}

/* ══════════════════════════════════════════════════════
   GENERAL TAB
══════════════════════════════════════════════════════ */
function GeneralTab() {
  const [active, setActive] = useState(true);
  const [desc, setDesc] = useState("");
  const [systemPrompt, setSystemPrompt] = useState(
    "You are a helpful AI assistant. Answer questions based only on the provided context. If you don't know the answer, say so politely and suggest contacting support."
  );
  const [fallback, setFallback] = useState(
    "I'm sorry, I don't have enough information to answer that. Please contact our support team for further assistance."
  );
  const [maxLen, setMaxLen] = useState(200);
  const [offHours, setOffHours] = useState(false);
  const [offMsg, setOffMsg] = useState(
    "We're currently offline. Please leave your message and we'll get back to you shortly."
  );

  return <>
    <SCard title="Chatbot Status" subtitle="Enable or disable this chatbot for all website visitors.">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800">Active</p>
          <p className="text-xs text-gray-500 mt-0.5">When disabled, the widget will be hidden from your website.</p>
        </div>
        <Toggle value={active} onChange={setActive} />
      </div>
    </SCard>

    <SCard title="Description" subtitle="For your reference only. Not shown to visitors.">
      <textarea
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        rows={2}
        placeholder="e.g. Main support chatbot for nexagpt.ai"
        className={TA}
      />
    </SCard>

    <SCard title="System Prompt" subtitle="Defines the bot's personality and rules. Only the AI sees this.">
      <textarea
        value={systemPrompt}
        onChange={(e) => setSystemPrompt(e.target.value)}
        rows={5}
        className={TA}
      />
      <p className="text-xs text-gray-400 text-right mt-1">{systemPrompt.length} chars</p>
    </SCard>

    <SCard title="Fallback Message" subtitle="Shown when the bot can't find a relevant answer in its knowledge base.">
      <textarea
        value={fallback}
        onChange={(e) => setFallback(e.target.value)}
        rows={3}
        className={TA}
      />
    </SCard>

    <SCard title="Max Response Length" subtitle={`Limit bot replies to approximately ${maxLen} words.`}>
      <input
        type="range" min={50} max={600} step={25} value={maxLen}
        onChange={(e) => setMaxLen(Number(e.target.value))}
        className="w-full accent-primary"
      />
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>50 words</span>
        <span className="font-semibold text-gray-700">{maxLen} words</span>
        <span>600 words</span>
      </div>
    </SCard>

    <SCard title="Off-Hours Message" subtitle="Send a custom reply when users contact you outside business hours.">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-700">Enable off-hours message</p>
        <Toggle value={offHours} onChange={setOffHours} />
      </div>
      {offHours && (
        <textarea value={offMsg} onChange={(e) => setOffMsg(e.target.value)} rows={3} className={TA} />
      )}
    </SCard>
  </>;
}

/* ══════════════════════════════════════════════════════
   LEAD CAPTURE TAB
══════════════════════════════════════════════════════ */
function LeadCaptureTab() {
  const [mode, setMode] = useState("optional");
  const [timing, setTiming] = useState("before");
  const [fields, setFields] = useState({ name: true, email: true, phone: false, company: false, website: false });
  const [customFields, setCustomFields] = useState([]);
  const [newField, setNewField] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [gdprText, setGdprText] = useState("I agree to the privacy policy and consent to having my data stored.");
  const [privacyUrl, setPrivacyUrl] = useState("");
  const [retention, setRetention] = useState("forever");

  const addCustomField = () => {
    if (newField.trim()) { setCustomFields([...customFields, newField.trim()]); setNewField(""); }
  };

  return <>
    <SCard title="Collection Mode" subtitle="Choose when and how user details are collected.">
      {[
        { val: "mandatory", label: "Mandatory",  desc: "User must fill in details before they can start chatting." },
        { val: "optional",  label: "Optional",   desc: "User can skip and start chatting without providing details." },
        { val: "disabled",  label: "Disabled",   desc: "No user data will be collected." },
      ].map(({ val, label, desc }) => (
        <RadioRow key={val} name="lc-mode" value={val} current={mode} onChange={setMode} label={label} desc={desc} />
      ))}
    </SCard>

    {mode !== "disabled" && <>
      <SCard title="Form Timing" subtitle="When should the form appear in the conversation?">
        {[
          { val: "before", label: "Before chat starts",    desc: "Show the form as soon as the user opens the widget." },
          { val: "after",  label: "After first message",   desc: "Let the user send one message first, then ask for details." },
        ].map(({ val, label, desc }) => (
          <RadioRow key={val} name="lc-timing" value={val} current={timing} onChange={setTiming} label={label} desc={desc} />
        ))}
      </SCard>

      <SCard title="Fields to Collect" subtitle="Choose which pieces of information to request from users.">
        <div className="space-y-2">
          {Object.entries(fields).map(([key, checked]) => (
            <CheckRow
              key={key}
              label={key.charAt(0).toUpperCase() + key.slice(1)}
              checked={checked}
              onChange={(v) => setFields({ ...fields, [key]: v })}
            />
          ))}
          {customFields.map((cf, i) => (
            <div key={i} className="flex items-center gap-2">
              <CheckRow label={cf} checked onChange={() => setCustomFields(customFields.filter((_, idx) => idx !== i))} />
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <input
            type="text" value={newField}
            onChange={(e) => setNewField(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCustomField()}
            placeholder="Add custom field label…"
            className={IN}
          />
          <button onClick={addCustomField} className="flex items-center gap-1 px-3 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90">
            <FiPlus size={13} /> Add
          </button>
        </div>
      </SCard>

      <SCard title="GDPR Consent" subtitle="Add a consent checkbox before the user submits the lead form.">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-700">Require consent checkbox</p>
          <Toggle value={gdpr} onChange={setGdpr} />
        </div>
        {gdpr && <>
          <label className="block text-xs font-medium text-gray-600 mb-1">Consent Text</label>
          <textarea value={gdprText} onChange={(e) => setGdprText(e.target.value)} rows={2} className={TA} />
          <label className="block text-xs font-medium text-gray-600 mt-3 mb-1">Privacy Policy URL</label>
          <input type="url" value={privacyUrl} onChange={(e) => setPrivacyUrl(e.target.value)} placeholder="https://yoursite.com/privacy" className={IN} />
        </>}
      </SCard>

      <SCard title="Data Retention" subtitle="How long collected lead data should be stored.">
        <select value={retention} onChange={(e) => setRetention(e.target.value)} className={SEL}>
          <option value="30d">30 days</option>
          <option value="90d">90 days</option>
          <option value="1y">1 year</option>
          <option value="forever">Forever</option>
        </select>
      </SCard>
    </>}
  </>;
}

/* ══════════════════════════════════════════════════════
   CHAT BEHAVIOR TAB
══════════════════════════════════════════════════════ */
function ChatBehaviorTab() {
  const [startMode, setStartMode] = useState("ai");
  const [streaming, setStreaming] = useState(true);
  const [typing, setTyping] = useState(true);
  const [readReceipts, setReadReceipts] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const [handoffEmail, setHandoffEmail] = useState("");
  const [autoClose, setAutoClose] = useState(false);
  const [autoCloseTimeout, setAutoCloseTimeout] = useState("30");
  const [sessionMemory, setSessionMemory] = useState("session");
  const [msgLimit, setMsgLimit] = useState(50);

  return <>
    <SCard title="Chat Start Mode" subtitle="How every new conversation begins.">
      {[
        { val: "ai",    label: "Always starts with AI",    desc: "The bot handles all conversations automatically." },
        { val: "human", label: "Always starts with Human", desc: "New conversations are routed to a live agent first." },
      ].map(({ val, label, desc }) => (
        <RadioRow key={val} name="cb-start" value={val} current={startMode} onChange={setStartMode} label={label} desc={desc} />
      ))}
    </SCard>

    <SCard title="Chat Features" subtitle="Toggle interactive and visual features of the chat window.">
      <ToggleRow label="Streaming Responses"  desc="Display bot replies word-by-word as they generate."        value={streaming}    onChange={setStreaming} />
      <div className="border-t my-2" />
      <ToggleRow label="Typing Indicator"     desc="Show animated dots while the bot is generating a reply."   value={typing}       onChange={setTyping} />
      <div className="border-t my-2" />
      <ToggleRow label="Read Receipts"        desc="Show when the user's message has been seen."               value={readReceipts}  onChange={setReadReceipts} />
    </SCard>

    <SCard title="Human Handoff" subtitle="Allow users to request a live agent during a conversation.">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-700">Enable human handoff</p>
        <Toggle value={handoff} onChange={setHandoff} />
      </div>
      {handoff && <>
        <label className="block text-xs font-medium text-gray-600 mb-1">Notification Email</label>
        <input
          type="email" value={handoffEmail}
          onChange={(e) => setHandoffEmail(e.target.value)}
          placeholder="agent@yourcompany.com"
          className={IN}
        />
        <p className="text-xs text-gray-400 mt-1">You'll be notified when a user requests a live agent.</p>
      </>}
    </SCard>

    <SCard title="Session Settings" subtitle="Control conversation memory and per-session message limits.">
      <p className="text-sm font-medium text-gray-800 mb-1">Session Memory</p>
      {[
        { val: "session",    label: "Per session",               desc: "Memory is cleared when the user closes the chat." },
        { val: "persistent", label: "Persistent across sessions", desc: "Users can pick up conversations where they left off." },
      ].map(({ val, label, desc }) => (
        <RadioRow key={val} name="cb-memory" value={val} current={sessionMemory} onChange={setSessionMemory} label={label} desc={desc} />
      ))}
      <div className="border-t my-3" />
      <p className="text-sm font-medium text-gray-800">
        Messages Per Session — <span className="text-primary">{msgLimit}</span>
      </p>
      <p className="text-xs text-gray-500 mb-2">Max number of messages a user can send in one session.</p>
      <input
        type="range" min={10} max={200} step={5} value={msgLimit}
        onChange={(e) => setMsgLimit(Number(e.target.value))}
        className="w-full accent-primary"
      />
      <div className="flex justify-between text-xs text-gray-400 mt-0.5"><span>10</span><span>200</span></div>
    </SCard>

    <SCard title="Auto-close Inactive Conversations" subtitle="Automatically close conversations with no activity.">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-700">Enable auto-close</p>
        <Toggle value={autoClose} onChange={setAutoClose} />
      </div>
      {autoClose && <>
        <label className="block text-xs font-medium text-gray-600 mb-1">Close after inactivity of</label>
        <select value={autoCloseTimeout} onChange={(e) => setAutoCloseTimeout(e.target.value)} className={SEL}>
          <option value="5">5 minutes</option>
          <option value="15">15 minutes</option>
          <option value="30">30 minutes</option>
          <option value="60">1 hour</option>
          <option value="240">4 hours</option>
          <option value="1440">24 hours</option>
        </select>
      </>}
    </SCard>
  </>;
}

/* ══════════════════════════════════════════════════════
   NOTIFICATIONS TAB
══════════════════════════════════════════════════════ */
function NotificationsTab() {
  const [newLeadEnabled, setNewLeadEnabled] = useState(true);
  const [newLeadAddr, setNewLeadAddr] = useState("");
  const [handoffEnabled, setHandoffEnabled] = useState(true);
  const [handoffAddr, setHandoffAddr] = useState("");
  const [convStarted, setConvStarted] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestDay, setDigestDay] = useState("monday");
  const [digestAddr, setDigestAddr] = useState("");
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [slackUrl, setSlackUrl] = useState("");
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");

  return <>
    <SCard title="Email Alerts" subtitle="Get notified by email when key events happen.">
      <ToggleRow label="New Lead Captured" desc="Email me when a user submits their contact details." value={newLeadEnabled} onChange={setNewLeadEnabled} />
      {newLeadEnabled && (
        <input type="email" value={newLeadAddr} onChange={(e) => setNewLeadAddr(e.target.value)} placeholder="you@company.com" className={`${IN} mt-2`} />
      )}
      <div className="border-t my-3" />
      <ToggleRow label="Human Handoff Requested" desc="Email me when a user asks to speak with a live agent." value={handoffEnabled} onChange={setHandoffEnabled} />
      {handoffEnabled && (
        <input type="email" value={handoffAddr} onChange={(e) => setHandoffAddr(e.target.value)} placeholder="agent@company.com" className={`${IN} mt-2`} />
      )}
      <div className="border-t my-3" />
      <ToggleRow label="New Conversation Started" desc="Email me whenever a new user starts a conversation." value={convStarted} onChange={setConvStarted} />
    </SCard>

    <SCard title="Weekly Digest" subtitle="Get a performance summary delivered to your inbox every week.">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-700">Enable weekly digest</p>
        <Toggle value={digestEnabled} onChange={setDigestEnabled} />
      </div>
      {digestEnabled && <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Day</label>
          <select value={digestDay} onChange={(e) => setDigestDay(e.target.value)} className={SEL}>
            {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map((d) => (
              <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Recipient Email</label>
          <input type="email" value={digestAddr} onChange={(e) => setDigestAddr(e.target.value)} placeholder="you@company.com" className={IN} />
        </div>
      </div>}
    </SCard>

    <SCard title="Slack Notifications" subtitle="Send event alerts to a Slack channel via incoming webhook.">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-700">Enable Slack notifications</p>
        <Toggle value={slackEnabled} onChange={setSlackEnabled} />
      </div>
      {slackEnabled && <>
        <label className="block text-xs font-medium text-gray-600 mb-1">Webhook URL</label>
        <input type="url" value={slackUrl} onChange={(e) => setSlackUrl(e.target.value)} placeholder="https://hooks.slack.com/services/…" className={IN} />
        <p className="text-xs text-gray-400 mt-1">Create a webhook at api.slack.com/apps and paste the URL above.</p>
      </>}
    </SCard>

    <SCard title="Custom Webhook" subtitle="POST event data to your own endpoint in real time.">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-700">Enable custom webhook</p>
        <Toggle value={webhookEnabled} onChange={setWebhookEnabled} />
      </div>
      {webhookEnabled && <>
        <label className="block text-xs font-medium text-gray-600 mb-1">Endpoint URL</label>
        <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://api.yourapp.com/nexagpt-webhook" className={IN} />
        <p className="text-xs text-gray-400 mt-1">
          NexaGPT will POST JSON for events: <code className="bg-gray-100 px-1 rounded text-gray-600">new_lead</code>, <code className="bg-gray-100 px-1 rounded text-gray-600">conversation_start</code>, <code className="bg-gray-100 px-1 rounded text-gray-600">handoff_request</code>.
        </p>
      </>}
    </SCard>
  </>;
}

/* ══════════════════════════════════════════════════════
   LOCALIZATION TAB
══════════════════════════════════════════════════════ */
function LocalizationTab() {
  const [language, setLanguage] = useState("en");
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  const [timeFormat, setTimeFormat] = useState("12h");
  const [currency, setCurrency] = useState("USD");
  const [bizHours, setBizHours] = useState(false);

  const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
  const [hours, setHours] = useState(() =>
    Object.fromEntries(DAYS.map((d) => [d, {
      enabled: d !== "Saturday" && d !== "Sunday",
      open: "09:00",
      close: "17:00",
    }]))
  );
  const updateHour = (day, field, val) =>
    setHours((h) => ({ ...h, [day]: { ...h[day], [field]: val } }));

  return <>
    <SCard title="Language" subtitle="The default language your chatbot responds in.">
      <select value={language} onChange={(e) => setLanguage(e.target.value)} className={SEL}>
        <option value="en">English</option>
        <option value="hi">Hindi</option>
        <option value="es">Spanish</option>
        <option value="fr">French</option>
        <option value="de">German</option>
        <option value="pt">Portuguese</option>
        <option value="ar">Arabic</option>
        <option value="zh">Chinese (Simplified)</option>
        <option value="ja">Japanese</option>
        <option value="ko">Korean</option>
        <option value="it">Italian</option>
        <option value="nl">Dutch</option>
        <option value="ru">Russian</option>
        <option value="tr">Turkish</option>
        <option value="auto">Auto-detect visitor language</option>
      </select>
    </SCard>

    <SCard title="Timezone" subtitle="Used for conversation timestamps and analytics reporting.">
      <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className={SEL}>
        <optgroup label="Asia">
          <option value="Asia/Kolkata">India Standard Time (IST) — UTC+5:30</option>
          <option value="Asia/Singapore">Singapore Time (SGT) — UTC+8:00</option>
          <option value="Asia/Tokyo">Japan Standard Time (JST) — UTC+9:00</option>
          <option value="Asia/Dubai">Gulf Standard Time (GST) — UTC+4:00</option>
        </optgroup>
        <optgroup label="Americas">
          <option value="America/New_York">Eastern Time (ET) — UTC−5:00</option>
          <option value="America/Chicago">Central Time (CT) — UTC−6:00</option>
          <option value="America/Denver">Mountain Time (MT) — UTC−7:00</option>
          <option value="America/Los_Angeles">Pacific Time (PT) — UTC−8:00</option>
          <option value="America/Sao_Paulo">Brazil Time (BRT) — UTC−3:00</option>
        </optgroup>
        <optgroup label="Europe">
          <option value="Europe/London">Greenwich Mean Time (GMT) — UTC+0:00</option>
          <option value="Europe/Paris">Central European Time (CET) — UTC+1:00</option>
          <option value="Europe/Moscow">Moscow Time (MSK) — UTC+3:00</option>
        </optgroup>
        <optgroup label="Pacific / Oceania">
          <option value="Australia/Sydney">Australian Eastern Time (AET) — UTC+11:00</option>
          <option value="Pacific/Auckland">New Zealand Time (NZST) — UTC+13:00</option>
        </optgroup>
      </select>
    </SCard>

    <SCard title="Date & Time Format" subtitle="How dates and times appear in conversation history.">
      <p className="text-xs font-medium text-gray-600 mb-2">Date Format</p>
      <div className="grid grid-cols-2 gap-y-2 gap-x-4 mb-4">
        {[
          { val: "MM/DD/YYYY",  label: "MM/DD/YYYY — e.g. 05/11/2026" },
          { val: "DD/MM/YYYY",  label: "DD/MM/YYYY — e.g. 11/05/2026" },
          { val: "YYYY-MM-DD",  label: "YYYY-MM-DD — e.g. 2026-05-11" },
          { val: "DD MMM YYYY", label: "DD MMM YYYY — e.g. 11 May 2026" },
        ].map((opt) => (
          <label key={opt.val} className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="loc-date" value={opt.val} checked={dateFormat === opt.val}
              onChange={(e) => setDateFormat(e.target.value)} className="accent-primary" />
            <span className="text-sm text-gray-700">{opt.label}</span>
          </label>
        ))}
      </div>
      <p className="text-xs font-medium text-gray-600 mb-2">Time Format</p>
      <div className="flex gap-6">
        {[{ val: "12h", label: "12-hour (2:30 PM)" }, { val: "24h", label: "24-hour (14:30)" }].map((opt) => (
          <label key={opt.val} className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="loc-time" value={opt.val} checked={timeFormat === opt.val}
              onChange={(e) => setTimeFormat(e.target.value)} className="accent-primary" />
            <span className="text-sm text-gray-700">{opt.label}</span>
          </label>
        ))}
      </div>
    </SCard>

    <SCard title="Currency" subtitle="Currency symbol shown in lead capture and billing fields.">
      <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={SEL}>
        <option value="USD">USD — US Dollar ($)</option>
        <option value="EUR">EUR — Euro (€)</option>
        <option value="GBP">GBP — British Pound (£)</option>
        <option value="INR">INR — Indian Rupee (₹)</option>
        <option value="AUD">AUD — Australian Dollar (A$)</option>
        <option value="CAD">CAD — Canadian Dollar (C$)</option>
        <option value="SGD">SGD — Singapore Dollar (S$)</option>
        <option value="AED">AED — UAE Dirham (د.إ)</option>
        <option value="JPY">JPY — Japanese Yen (¥)</option>
        <option value="BRL">BRL — Brazilian Real (R$)</option>
      </select>
    </SCard>

    <SCard title="Business Hours" subtitle="Define when your team is available. Used to trigger off-hours messages.">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-700">Enable business hours schedule</p>
        <Toggle value={bizHours} onChange={setBizHours} />
      </div>
      {bizHours && (
        <div className="space-y-2.5">
          {DAYS.map((day) => (
            <div key={day} className="flex items-center gap-3">
              <div className="w-28 flex items-center gap-2 shrink-0">
                <input
                  type="checkbox"
                  checked={hours[day].enabled}
                  onChange={(e) => updateHour(day, "enabled", e.target.checked)}
                  className="accent-primary"
                />
                <span className={`text-sm w-16 ${hours[day].enabled ? "text-gray-800 font-medium" : "text-gray-400"}`}>
                  {day.slice(0, 3)}
                </span>
              </div>
              {hours[day].enabled ? (
                <div className="flex items-center gap-2">
                  <input
                    type="time" value={hours[day].open}
                    onChange={(e) => updateHour(day, "open", e.target.value)}
                    className="border rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <span className="text-xs text-gray-400">to</span>
                  <input
                    type="time" value={hours[day].close}
                    onChange={(e) => updateHour(day, "close", e.target.value)}
                    className="border rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              ) : (
                <span className="text-xs text-gray-400 italic">Closed</span>
              )}
            </div>
          ))}
        </div>
      )}
    </SCard>
  </>;
}

/* ══════════════════════════════════════════════════════
   ADVANCED TAB
══════════════════════════════════════════════════════ */
function AdvancedTab() {
  const [model, setModel] = useState("gpt-4o");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [temperature, setTemperature] = useState(0.3);
  const [topK, setTopK] = useState(4);
  const [maxTokens, setMaxTokens] = useState(512);
  const [confidence, setConfidence] = useState(0.5);
  const [domains, setDomains] = useState(["app.nexagpt.ai"]);
  const [newDomain, setNewDomain] = useState("");
  const [rateLimit, setRateLimit] = useState(false);
  const [ratePerMin, setRatePerMin] = useState(10);
  const [spamProtection, setSpamProtection] = useState(true);

  const addDomain = () => {
    const d = newDomain.trim().replace(/^https?:\/\//, "");
    if (d && !domains.includes(d)) { setDomains([...domains, d]); setNewDomain(""); }
  };

  return <>
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
      <FiAlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-amber-800">Advanced Settings</p>
        <p className="text-xs text-amber-700 mt-0.5">Only change these if you know what you're doing. Incorrect values may degrade bot quality.</p>
      </div>
    </div>

    <SCard title="AI Model" subtitle="Choose the language model that powers this chatbot.">
      <select value={model} onChange={(e) => setModel(e.target.value)} className={SEL}>
        <optgroup label="OpenAI">
          <option value="gpt-4o">GPT-4o (Recommended)</option>
          <option value="gpt-4-turbo">GPT-4 Turbo</option>
          <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fastest)</option>
        </optgroup>
        <optgroup label="Anthropic">
          <option value="claude-3-5-sonnet">Claude 3.5 Sonnet</option>
          <option value="claude-3-haiku">Claude 3 Haiku</option>
        </optgroup>
        <optgroup label="Google">
          <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
          <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
        </optgroup>
      </select>
    </SCard>

    <SCard title="Custom API Key" subtitle="Use your own LLM API key instead of NexaGPT's shared quota.">
      <div className="relative">
        <input
          type={showKey ? "text" : "password"}
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="sk-••••••••••••••••••••••••••••••••••••"
          className={`${IN} pr-10`}
        />
        <button
          type="button"
          onClick={() => setShowKey(!showKey)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {showKey ? <FiEyeOff size={15} /> : <FiEye size={15} />}
        </button>
      </div>
      <p className="text-xs text-gray-400 mt-1">Your key is encrypted at rest and never exposed to end users.</p>
    </SCard>

    <SCard title="Model Tuning" subtitle="Fine-tune how the AI generates responses.">
      <SliderField
        label="Temperature" value={temperature} min={0} max={1} step={0.05}
        onChange={setTemperature}
        hint="Lower = more factual. Higher = more creative."
        minLabel="Factual (0.0)" maxLabel="Creative (1.0)"
      />
      <div className="border-t my-4" />
      <SliderField
        label="Top-K Context Chunks" value={topK} min={1} max={8} step={1}
        onChange={setTopK}
        hint="Number of knowledge chunks fetched per query. Higher = more context, slower."
        minLabel="1 chunk" maxLabel="8 chunks"
      />
      <div className="border-t my-4" />
      <SliderField
        label="Max Response Tokens" value={maxTokens} min={128} max={2048} step={64}
        onChange={setMaxTokens}
        hint="Hard cap on tokens per reply (roughly ¾ of a word per token)."
        minLabel="128" maxLabel="2048"
      />
      <div className="border-t my-4" />
      <SliderField
        label="Confidence Threshold" value={confidence} min={0} max={1} step={0.05}
        onChange={setConfidence}
        hint="If relevance score is below this, the fallback message is shown."
        minLabel="0.0 (always answer)" maxLabel="1.0 (strict)"
      />
    </SCard>

    <SCard title="Allowed Domains" subtitle="Restrict the widget to specific domains. Leave empty to allow all.">
      <div className="flex flex-wrap gap-2 mb-3">
        {domains.map((d) => (
          <span key={d} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full">
            {d}
            <button onClick={() => setDomains(domains.filter((x) => x !== d))} className="text-gray-400 hover:text-red-500 transition ml-0.5">
              <FiX size={10} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text" value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addDomain()}
          placeholder="example.com"
          className={IN}
        />
        <button onClick={addDomain} className="flex items-center gap-1 px-3 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90">
          <FiPlus size={13} /> Add
        </button>
      </div>
    </SCard>

    <SCard title="Security" subtitle="Protect your chatbot from abuse and spam.">
      <ToggleRow label="Spam Protection" desc="Detect and filter repetitive or automated messages." value={spamProtection} onChange={setSpamProtection} />
      <div className="border-t my-3" />
      <ToggleRow label="Rate Limiting" desc="Limit the number of messages a user can send per minute." value={rateLimit} onChange={setRateLimit} />
      {rateLimit && (
        <div className="mt-3">
          <SliderField
            label="Messages per minute" value={ratePerMin} min={2} max={30} step={1}
            onChange={setRatePerMin} minLabel="2" maxLabel="30"
          />
        </div>
      )}
    </SCard>

    {/* Danger Zone */}
    <div className="bg-white border border-red-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-red-200 bg-red-50">
        <h2 className="font-semibold text-red-700">Danger Zone</h2>
        <p className="text-xs text-red-500 mt-0.5">These actions are irreversible. Proceed with extreme caution.</p>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-800">Reset All Training Data</p>
            <p className="text-xs text-gray-500 mt-0.5">Removes all trained sources. The chatbot will stop answering until you re-train it.</p>
          </div>
          <button className="shrink-0 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition whitespace-nowrap">
            Reset Training
          </button>
        </div>
        <div className="border-t" />
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-800">Delete Chatbot</p>
            <p className="text-xs text-gray-500 mt-0.5">Permanently deletes this chatbot and all its data — conversations, leads, and training.</p>
          </div>
          <button className="shrink-0 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition whitespace-nowrap">
            Delete Chatbot
          </button>
        </div>
      </div>
    </div>
  </>;
}

/* ══════════════════════════════════════════════════════
   SHARED HELPER COMPONENTS
══════════════════════════════════════════════════════ */

function SCard({ title, subtitle, children }) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b">
        <h2 className="font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${value ? "bg-primary" : "bg-gray-200"}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? "translate-x-5" : ""}`} />
    </button>
  );
}

function ToggleRow({ label, desc, value, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {desc && <p className="text-xs text-gray-500 mt-0.5">{desc}</p>}
      </div>
      <Toggle value={value} onChange={onChange} />
    </div>
  );
}

function RadioRow({ name, value, current, onChange, label, desc }) {
  return (
    <label className="flex gap-3 items-start cursor-pointer py-1.5">
      <input
        type="radio" name={name} value={value}
        checked={current === value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 accent-primary shrink-0"
      />
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {desc && <p className="text-xs text-gray-500 mt-0.5">{desc}</p>}
      </div>
    </label>
  );
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer py-0.5">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-primary" />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

function SliderField({ label, value, min, max, step, onChange, hint, minLabel, maxLabel }) {
  return (
    <div>
      <div className="flex justify-between mb-1">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <span className="text-sm font-semibold text-primary">{value}</span>
      </div>
      {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
      />
      {(minLabel || maxLabel) && (
        <div className="flex justify-between text-xs text-gray-400 mt-0.5">
          <span>{minLabel}</span><span>{maxLabel}</span>
        </div>
      )}
    </div>
  );
}

/* ── Shared class strings ── */
const TA  = "w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-gray-800 resize-none";
const IN  = "w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-gray-800";
const SEL = "w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-gray-800 bg-white";
